import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getPatternPerformanceStats, getPatternAlerts } from '@/utils/localStorage';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, ComposedChart, Area } from 'recharts';
import { History, Target, TrendingUp, Award, Activity, Zap, CheckCircle, XCircle, AlertCircle, BarChart3 } from 'lucide-react';
import { format } from 'date-fns';

export default function PatternPerformance() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = () => {
    const performanceStats = getPatternPerformanceStats();
    const allAlerts = getPatternAlerts();
    setStats(performanceStats);
    setAlerts(allAlerts);
  };
  
  const getAccuracyColor = (accuracy) => {
    const acc = parseFloat(accuracy);
    if (acc >= 70) return '#10B981'; // success
    if (acc >= 50) return '#3B82F6'; // primary
    if (acc >= 30) return '#F59E0B'; // warning
    return '#EF4444'; // destructive
  };
  
  const getPatternLevelBadge = (level) => {
    switch (level) {
      case 'high':
        return { icon: '🟢', label: 'High', className: 'bg-success/20 text-success border-success/50' };
      case 'moderate':
        return { icon: '🟡', label: 'Moderate', className: 'bg-warning/20 text-warning border-warning/50' };
      case 'low':
        return { icon: '🔴', label: 'Low', className: 'bg-destructive/20 text-destructive border-destructive/50' };
      default:
        return { icon: '⚪', label: 'Unknown', className: 'bg-muted/20 text-muted-foreground border-muted/50' };
    }
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <History className="w-10 h-10 text-primary" />
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground">
            Pattern Performance
          </h1>
        </div>
        <p className="text-muted-foreground font-medium">
          Track how accurate Signal Pattern Alerts have been and measure their real effectiveness.
        </p>
      </div>
      
      {stats && stats.totalAlerts > 0 ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className="p-6 border-border/40 bg-card" data-testid="total-alerts-card">
              <div className="flex items-center space-x-2 mb-1">
                <Zap className="w-4 h-4 text-primary" />
                <p className="text-sm text-muted-foreground">Total Alerts</p>
              </div>
              <p className="text-3xl font-black font-mono">{stats.totalAlerts}</p>
              <p className="text-xs text-muted-foreground mt-1">Pattern alerts generated</p>
            </Card>
            
            <Card className="p-6 border-border/40 bg-card" data-testid="trades-taken-card">
              <div className="flex items-center space-x-2 mb-1">
                <Target className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Trades Taken</p>
              </div>
              <p className="text-3xl font-black font-mono text-primary">{stats.tradesTaken}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.totalAlerts > 0 
                  ? `${((stats.tradesTaken / stats.totalAlerts) * 100).toFixed(0)}% conversion`
                  : '0% conversion'
                }
              </p>
            </Card>
            
            <Card className="p-6 border-border/40 bg-card" data-testid="win-rate-card">
              <div className="flex items-center space-x-2 mb-1">
                <Award className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Win Rate</p>
              </div>
              <p className="text-3xl font-black font-mono" style={{ color: getAccuracyColor(stats.winRate) }}>
                {stats.winRate}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">{stats.wins}W / {stats.losses}L</p>
            </Card>
            
            <Card className={`p-6 border-border/40 ${parseFloat(stats.accuracy) >= 50 ? 'bg-success/10' : 'bg-destructive/10'}`} data-testid="accuracy-card">
              <div className="flex items-center space-x-2 mb-1">
                <CheckCircle className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Accuracy</p>
              </div>
              <p className="text-3xl font-black font-mono" style={{ color: getAccuracyColor(stats.accuracy) }}>
                {stats.accuracy}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Prediction accuracy</p>
            </Card>
          </div>
          
          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Alerts vs Successful Trades Chart */}
            <Card className="p-6 border-border/40 bg-card" data-testid="alerts-trades-chart">
              <h2 className="text-xl font-bold text-foreground mb-4">Alerts vs Successful Trades</h2>
              {stats.timelineData && stats.timelineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={stats.timelineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="date" stroke="#A1A1AA" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#A1A1AA" />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      labelStyle={{ color: '#FAFAFA' }}
                    />
                    <Legend />
                    <Bar dataKey="alerts" name="Alerts" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="wins" name="Wins" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="trades" name="Trades Taken" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B', r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  <p>Not enough data for timeline chart</p>
                </div>
              )}
            </Card>
            
            {/* Pattern Accuracy Chart */}
            <Card className="p-6 border-border/40 bg-card" data-testid="pattern-accuracy-chart">
              <h2 className="text-xl font-bold text-foreground mb-4">Pattern Accuracy by Type</h2>
              {stats.byPattern && stats.byPattern.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.byPattern} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis type="number" stroke="#A1A1AA" domain={[0, 100]} />
                    <YAxis dataKey="pattern" type="category" stroke="#A1A1AA" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      labelStyle={{ color: '#FAFAFA' }}
                      formatter={(value) => [`${value}%`, 'Win Rate']}
                    />
                    <Bar dataKey="winRate" name="Win Rate %" radius={[0, 4, 4, 0]}>
                      {stats.byPattern.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getAccuracyColor(entry.winRate)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  <p>No pattern data available</p>
                </div>
              )}
            </Card>
          </div>
          
          {/* Pattern Breakdown Table */}
          <Card className="p-6 border-border/40 bg-card mb-6" data-testid="pattern-breakdown-table">
            <div className="flex items-center space-x-2 mb-4">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-bold text-foreground">Pattern Breakdown</h2>
            </div>
            
            {stats.byPattern && stats.byPattern.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border/40">
                    <tr className="text-left">
                      <th className="p-4 font-bold text-sm text-muted-foreground">Pattern</th>
                      <th className="p-4 font-bold text-sm text-muted-foreground">Alerts</th>
                      <th className="p-4 font-bold text-sm text-muted-foreground">Trades Taken</th>
                      <th className="p-4 font-bold text-sm text-muted-foreground">Wins</th>
                      <th className="p-4 font-bold text-sm text-muted-foreground">Losses</th>
                      <th className="p-4 font-bold text-sm text-muted-foreground">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.byPattern.map((pattern, index) => (
                      <tr 
                        key={index} 
                        className="border-b border-border/20 hover:bg-accent/50 transition-colors"
                        data-testid={`pattern-row-${index}`}
                      >
                        <td className="p-4">
                          <span className="font-semibold text-foreground capitalize">{pattern.pattern}</span>
                        </td>
                        <td className="p-4">
                          <span className="font-mono font-semibold">{pattern.alerts}</span>
                        </td>
                        <td className="p-4">
                          <span className="font-mono font-semibold">{pattern.tradesTaken}</span>
                        </td>
                        <td className="p-4">
                          <span className="font-mono font-semibold text-success">{pattern.wins}</span>
                        </td>
                        <td className="p-4">
                          <span className="font-mono font-semibold text-destructive">{pattern.losses}</span>
                        </td>
                        <td className="p-4">
                          <span 
                            className="font-mono font-bold text-lg"
                            style={{ color: getAccuracyColor(pattern.accuracy) }}
                          >
                            {pattern.accuracy}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No pattern data available</p>
            )}
          </Card>
          
          {/* Recent Alerts History */}
          <Card className="p-6 border-border/40 bg-card" data-testid="alerts-history">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Activity className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold text-foreground">Recent Pattern Alerts</h2>
              </div>
              <Button onClick={loadData} variant="ghost" size="sm" data-testid="refresh-alerts-btn">
                Refresh
              </Button>
            </div>
            
            {alerts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border/40">
                    <tr className="text-left">
                      <th className="p-3 font-bold text-sm text-muted-foreground">Time</th>
                      <th className="p-3 font-bold text-sm text-muted-foreground">Asset</th>
                      <th className="p-3 font-bold text-sm text-muted-foreground">Signal</th>
                      <th className="p-3 font-bold text-sm text-muted-foreground">Timeframe</th>
                      <th className="p-3 font-bold text-sm text-muted-foreground">Pattern Level</th>
                      <th className="p-3 font-bold text-sm text-muted-foreground">Historical Win Rate</th>
                      <th className="p-3 font-bold text-sm text-muted-foreground">Trade Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.slice(0, 20).map((alert, index) => {
                      const levelBadge = getPatternLevelBadge(alert.patternLevel);
                      return (
                        <tr 
                          key={alert.id} 
                          className="border-b border-border/20 hover:bg-accent/50 transition-colors"
                          data-testid={`alert-row-${index}`}
                        >
                          <td className="p-3 font-mono text-xs">
                            {format(new Date(alert.timestamp), 'MMM dd, HH:mm')}
                          </td>
                          <td className="p-3">
                            <span className="font-bold text-foreground">{alert.asset}</span>
                          </td>
                          <td className="p-3">
                            <span className="capitalize text-sm">{alert.signalType}</span>
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-secondary/50">
                              {alert.timeframe}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-bold border ${levelBadge.className}`}>
                              {levelBadge.icon} {levelBadge.label}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="font-mono font-semibold">{alert.historicalWinRate}%</span>
                          </td>
                          <td className="p-3">
                            {alert.tradeLinked ? (
                              <span className={`flex items-center space-x-1 ${
                                alert.tradeResult === 'win' ? 'text-success' : 'text-destructive'
                              }`}>
                                {alert.tradeResult === 'win' ? (
                                  <><CheckCircle className="w-4 h-4" /><span className="font-semibold">WIN</span></>
                                ) : (
                                  <><XCircle className="w-4 h-4" /><span className="font-semibold">LOSS</span></>
                                )}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs flex items-center space-x-1">
                                <AlertCircle className="w-3 h-3" />
                                <span>Not taken</span>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No alerts recorded yet</p>
            )}
          </Card>
        </>
      ) : (
        <Card className="p-12 border-border/40 bg-card text-center" data-testid="empty-state">
          <History className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-20" />
          <p className="text-muted-foreground font-medium mb-2">No pattern alerts recorded yet</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Pattern alerts are automatically tracked when signals match high-performing patterns. 
            Go to Signal Scanner and wait for incoming signals that match your historical trading patterns.
          </p>
        </Card>
      )}
    </div>
  );
}
